'use client';

import { useRef } from 'react';

import { useSearchParams } from 'next/navigation';

import {
  useTalentMicrofrontendTalentAuthContextGetUserDataQueryQuery,
  type TalentUserFragmentFragment,
} from './graphql/GetUserData.v5.generated';

export type TTalentUser = TalentUserFragmentFragment & {
  user_parent_id?: number | null;
};

// These props are only used for testing or storybook envs
type TOptions = {
  companyId?: number;
  userId?: number;
  employeeId?: number;
  userParentId?: number;
};

export const useGetUserData = ({
  companyId,
  userId,
  employeeId,
  userParentId,
}: TOptions = {}) => {
  const searchParams = useSearchParams();

  const employeeIdRef = useRef(
    parseInt(searchParams.get('employeeId') as string),
  );
  const companyIdRef = useRef(
    parseInt(searchParams.get('companyId') as string),
  );
  const userIdRef = useRef(
    parseInt(searchParams.get('userId') as string) || null,
  );
  // const adminId = parseInt(queryParams.get('adminId') as string) || null;

  const userParentIdRef = useRef(
    parseInt(searchParams.get('userParentId') as string) || null,
  );

  const variables = {
    input: {
      employee_id: employeeId || employeeIdRef.current,
      user_id: userId || userIdRef.current || employeeIdRef.current,
      company_id: companyId || companyIdRef.current,
      user_parent_id: userParentId || userParentIdRef.current,
    },
  };

  const authResponse =
    useTalentMicrofrontendTalentAuthContextGetUserDataQueryQuery({
      variables,
      context: { clientName: 'v5' },
    });

  if (authResponse.loading || authResponse.error) {
    return {
      ...authResponse,
      user: null,
      queryParamsIds: null,
    };
  }

  if (!authResponse.data?.talent.talent_get_user_profile) {
    return {
      ...authResponse,
      user: null,
      queryParamsIds: null,
      error: new Error(
        `No user found with the following data: ${JSON.stringify({
          employee_id: employeeIdRef.current || null,
          user_id: userIdRef.current || employeeIdRef.current || null,
          company_id: companyIdRef.current || null,
          user_parent_id: userParentIdRef.current,
        })}`,
      ),
    };
  }

  const user: TTalentUser = {
    ...authResponse.data.talent.talent_get_user_profile,
    user_parent_id: variables.input.user_parent_id,
  };
  return {
    ...authResponse,
    user,
    queryParamsIds: variables.input,
    loading: false,
  };
};
